import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import type { AdminPlan, AdminUser } from '../../types';

// ============================================================================
// LIST + actions (soft delete / restore / send-reset)
// ============================================================================

export function UsersPage() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  async function reload() {
    try {
      const url = `/api/admin/users${showDeleted ? '?includeDeleted=true' : ''}`;
      setUsers(await api<AdminUser[]>(url));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    }
  }
  useEffect(() => { void reload(); }, [showDeleted]);

  async function softDelete(u: AdminUser) {
    if (!confirm(`Soft-delete ${u.email}? They won't be able to sign in.`)) return;
    try { await api(`/api/admin/users/${u.id}`, { method: 'DELETE' }); await reload(); }
    catch (err) { alert(err instanceof Error ? err.message : 'Failed'); }
  }
  async function restore(u: AdminUser) {
    try { await api(`/api/admin/users/${u.id}/restore`, { method: 'POST' }); await reload(); }
    catch (err) { alert(err instanceof Error ? err.message : 'Failed'); }
  }
  async function sendReset(u: AdminUser) {
    if (!confirm(`Send a password-reset email to ${u.email}?`)) return;
    try { await api(`/api/admin/users/${u.id}/send-reset`, { method: 'POST' }); alert('Sent.'); }
    catch (err) { alert(err instanceof Error ? err.message : 'Failed (check /admin/email config)'); }
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header">
        <h1>Users</h1>
        <Link to="/admin/users/new" className="btn-primary">New user</Link>
      </header>

      <label className="form-checkbox" style={{ marginBottom: 12 }}>
        <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
        Show deleted users
      </label>

      {error && <div className="form-error">{error}</div>}
      {!users && !error && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {users && (
        <table className="admin-table">
          <thead>
            <tr><th>Email</th><th>Name</th><th>Plan</th><th>Admin</th><th>Verified</th><th>Created</th><th></th></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const deleted = u.deletedAt !== null;
              return (
                <tr key={u.id} style={deleted ? { opacity: 0.6 } : undefined}>
                  <td>{u.email}{deleted && ' (deleted)'}</td>
                  <td>{u.name}</td>
                  <td>{u.plan?.name ?? '—'}</td>
                  <td>{u.isAdmin ? '✓' : '—'}</td>
                  <td>{u.emailVerifiedAt ? '✓' : '—'}</td>
                  <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="admin-table-actions">
                    {deleted ? (
                      <button type="button" className="btn-link" onClick={() => void restore(u)}>Restore</button>
                    ) : (
                      <>
                        <Link to={`/admin/users/${u.id}`} className="btn-link">Edit</Link>
                        <button type="button" className="btn-link" onClick={() => void sendReset(u)}>Send reset</button>
                        <button type="button" className="btn-link" onClick={() => void softDelete(u)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ============================================================================
// CREATE
// ============================================================================

interface UserAddForm {
  email: string;
  name: string;
  password: string;
  planId: string;
  isAdmin: boolean;
  sendInvite: boolean;
}
const EMPTY_ADD: UserAddForm = { email: '', name: '', password: '', planId: '', isAdmin: false, sendInvite: true };

export function UserAddPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<UserAddForm>(EMPTY_ADD);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const list = await api<AdminPlan[]>('/api/admin/plans');
        setPlans(list);
        const free = list.find((p) => p.slug === 'free') ?? list[0];
        if (free) setData((d) => ({ ...d, planId: free.id }));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load plans');
      }
    })();
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!data.sendInvite && data.password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await api('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          name: data.name,
          password: data.sendInvite ? '' : data.password,
          planId: data.planId || null,
          isAdmin: data.isAdmin,
          sendInvite: data.sendInvite,
        }),
      });
      navigate('/admin/users');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      if (msg === 'email_already_exists') setError('That email is already registered.');
      else if (msg === 'invalid_email') setError('That email looks malformed.');
      else if (msg === 'password_too_short') setError('Password must be at least 12 characters.');
      else setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header"><h1>New user</h1></header>
      {error && <div className="form-error">{error}</div>}
      <form onSubmit={(e) => void onSubmit(e)} className="admin-form">
        <div className="form-row-pair">
          <div className="form-group">
            <label>Email</label>
            <input type="email" required value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} disabled={submitting} autoComplete="off" />
          </div>
          <div className="form-group">
            <label>Name</label>
            <input type="text" required value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} disabled={submitting} autoComplete="off" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-checkbox">
            <input type="checkbox" checked={data.sendInvite} onChange={(e) => setData({ ...data, sendInvite: e.target.checked })} disabled={submitting} />
            Send invite email — they'll set their own password
          </label>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Requires SMTP configured at <Link to="/admin/email" className="btn-link" style={{ padding: 0 }}>/admin/email</Link>.
          </p>
        </div>

        {!data.sendInvite && (
          <div className="form-group">
            <label>Password</label>
            <input type="text" required minLength={12} value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })} disabled={submitting} autoComplete="new-password" placeholder="At least 12 characters — share with the user out-of-band" />
          </div>
        )}

        <div className="form-row-pair">
          <div className="form-group">
            <label>Plan</label>
            <select className="admin-select" value={data.planId} onChange={(e) => setData({ ...data, planId: e.target.value })} disabled={submitting}>
              <option value="">— no plan —</option>
              {plans.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          </div>
          <div className="form-group" style={{ alignSelf: 'end' }}>
            <label className="form-checkbox">
              <input type="checkbox" checked={data.isAdmin} onChange={(e) => setData({ ...data, isAdmin: e.target.checked })} disabled={submitting} />
              Admin (full access to /admin)
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/users')} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Creating…' : data.sendInvite ? 'Create user & send invite' : 'Create user'}
          </button>
        </div>
      </form>
    </section>
  );
}

// ============================================================================
// EDIT
// ============================================================================

interface UserEditForm {
  email: string;
  name: string;
  planId: string;
  isAdmin: boolean;
  newPassword: string;
}

export function UserEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState<UserEditForm | null>(null);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [u, ps] = await Promise.all([
          api<AdminUser>(`/api/admin/users/${id}`),
          api<AdminPlan[]>('/api/admin/plans'),
        ]);
        setPlans(ps);
        const planId = ps.find((p) => p.slug === u.plan?.slug)?.id ?? '';
        setData({ email: u.email, name: u.name, planId, isAdmin: u.isAdmin, newPassword: '' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Load failed');
      }
    })();
  }, [id]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    setError(null);
    if (data.newPassword && data.newPassword.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        email: data.email,
        name: data.name,
        planId: data.planId || null,
        isAdmin: data.isAdmin,
      };
      if (data.newPassword) body.password = data.newPassword;
      await api(`/api/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      navigate('/admin/users');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      if (msg === 'email_already_exists') setError('That email is already registered.');
      else if (msg === 'invalid_email') setError('That email looks malformed.');
      else if (msg === 'cannot_demote_self') setError('You cannot remove your own admin status.');
      else if (msg === 'password_too_short') setError('Password must be at least 12 characters.');
      else setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!data) {
    return (
      <section className="admin-page">
        {error ? <div className="form-error">{error}</div> : <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      </section>
    );
  }

  const isSelf = me?.id === id;

  return (
    <section className="admin-page">
      <header className="admin-page-header"><h1>Edit user</h1></header>
      {error && <div className="form-error">{error}</div>}
      <form onSubmit={(e) => void onSubmit(e)} className="admin-form">
        <div className="form-row-pair">
          <div className="form-group">
            <label>Email</label>
            <input type="email" required value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} disabled={submitting} />
          </div>
          <div className="form-group">
            <label>Name</label>
            <input type="text" required value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} disabled={submitting} />
          </div>
        </div>

        <div className="form-row-pair">
          <div className="form-group">
            <label>Plan</label>
            <select className="admin-select" value={data.planId} onChange={(e) => setData({ ...data, planId: e.target.value })} disabled={submitting}>
              <option value="">— no plan —</option>
              {plans.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          </div>
          <div className="form-group" style={{ alignSelf: 'end' }}>
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={data.isAdmin}
                onChange={(e) => setData({ ...data, isAdmin: e.target.checked })}
                disabled={submitting || (isSelf && data.isAdmin)}
                title={isSelf ? "You can't remove your own admin status." : undefined}
              />
              Admin
            </label>
          </div>
        </div>

        <fieldset className="form-fieldset">
          <legend>Set new password (optional)</legend>
          <div className="form-group">
            <input
              type="text"
              minLength={12}
              value={data.newPassword}
              onChange={(e) => setData({ ...data, newPassword: e.target.value })}
              disabled={submitting}
              autoComplete="new-password"
              placeholder="Leave blank to keep current password"
            />
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
              Or click "Send reset" from the users list to email a reset link instead.
            </p>
          </div>
        </fieldset>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/users')} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </section>
  );
}
